import * as React from 'react';
import type {
  FieldError,
  GravityFormField,
  SupportedFieldType,
} from '../types';
import type { FieldComponentProps } from './parts';
import { InputField } from './Input';
import { TextareaField } from './Textarea';
import { RadioField } from './Radio';
import { CheckboxField } from './Checkbox';
import { SelectField } from './Select';
import { NameField } from './Name';
import { FileUploadField } from './FileUpload';
import { ConsentField } from './Consent';

/**
 * Field-type → renderer map. The 12 supported types are Sonic parity
 * (TEXT/EMAIL/PHONE/TEXTAREA/RADIO/FILEUPLOAD/CONSENT) plus SELECT, CHECKBOX,
 * NAME, NUMBER, DATE. Types outside this map (SECTION/HTML/HIDDEN/CAPTCHA/
 * MULTISELECT) are intentionally skipped without console noise.
 */
export const FIELD_COMPONENTS: Record<
  SupportedFieldType,
  React.ComponentType<FieldComponentProps>
> = {
  TEXT: InputField,
  EMAIL: InputField,
  PHONE: InputField,
  NUMBER: InputField,
  DATE: InputField,
  TEXTAREA: TextareaField,
  RADIO: RadioField,
  CHECKBOX: CheckboxField,
  SELECT: SelectField,
  NAME: NameField,
  FILEUPLOAD: FileUploadField,
  CONSENT: ConsentField,
};

interface FieldsProps {
  fields: GravityFormField[];
  formId: number;
  errors?: FieldError[];
}

/** Renders the form's supported fields as a semantic list, one `<li>` per field. */
export function Fields({ fields, formId, errors }: FieldsProps) {
  return (
    <ul className='m-0 flex list-none flex-col gap-md p-0'>
      {fields.map((field) => {
        const Field = FIELD_COMPONENTS[field.type as SupportedFieldType];
        if (!Field) return null;

        const error = errors?.find(({ id }) => id === field.id);

        return (
          <li className='m-0 p-0' key={field.id}>
            <Field field={field} formId={formId} error={error} />
          </li>
        );
      })}
    </ul>
  );
}
